/**
 * WoodAlps Homepage - Main Landing Page
 * 
 * The homepage showcases WoodAlps wooden bicycles with:
 * - Hero section with dynamic background and call-to-action
 * - Company introduction with Sanity CMS content
 * - Product lineup featuring bike categories
 * - Contact section for customer engagement
 * - Development debug tools for Sanity data inspection
 * 
 * Features:
 * - Server-side rendering with ISR (Incremental Static Regeneration)
 * - Responsive design with smooth scrolling
 * - Sanity CMS integration for dynamic content
 * - Image optimization for hero backgrounds
 * 
 * @description Main landing page with hero section, product showcase, and company information
 */

import Head from 'next/head';
import Image from 'next/image';
import IntroSection from '../components/sections/IntroSection';
import ProductLineupSection from '../components/sections/ProductLineupSection';
import ContactSection from '../components/sections/ContactSection';
import { ChevronDown } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Layout from '../components/layout/Layout';
import { getHomePageData, } from '../src/app/studio/lib/sanity';
import { urlForImage } from '../src/app/studio/lib/image';
import { sanityLoader } from '../utils/sanityLoader';
import { useTranslation } from '../src/hooks/useTranslation';
import SEOHead from '../components/seo/SEOHead';

/**
 * Development Debug Component
 * Shows Sanity data structure for debugging in development mode only
 */
const SanityDebug = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  if (process.env.NODE_ENV !== 'development') return null;
  
  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '10px',
      zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.7)',
      color: 'white',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      maxWidth: '300px',
      maxHeight: '200px',
      overflow: 'auto'
    }}>
      <button 
        onClick={() => setIsVisible(!isVisible)}
        style={{ 
          background: 'none', 
          border: 'none', 
          color: 'white', 
          cursor: 'pointer',
          fontWeight: 'bold',
          padding: '0',
          margin: '0'
        }}
      >
        Sanity Debug {isVisible ? '▲' : '▼'}
      </button>
      
      {isVisible && (
        <div>
          <p>Connection: {data ? '✅ Connected' : '❌ Not connected'}</p>
          {data && (
            <>
              <p>Title: {data.title || 'None'}</p>
              <p>Hero image: {data?.heroSection?.backgroundImage ? '✅' : '❌'}</p>
              <pre style={{ fontSize: '10px' }}>
                {JSON.stringify({
                  dataTypes: Object.keys(data || {}),
                  heroSection: data?.heroSection ? Object.keys(data.heroSection) : 'None'
                }, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default function Home({ pageData }) {
  const { t } = useTranslation();
  const router = useRouter();
  const introSectionRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Get header height on mount and window resize
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        // Get the full height including navigation
        setHeaderHeight(header.offsetHeight + 40); // Adding 40px for the navigation bar below
      }
    };

    // Initial calculation
    updateHeaderHeight();
    
    // Update on resize
    window.addEventListener('resize', updateHeaderHeight);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Custom smooth scroll function
  const smoothScrollTo = (targetPosition, duration = 500) => {
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    let startTime = null;

    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function - ease-in-out cubic
      const ease = t => t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1;
      
      window.scrollTo(0, startPosition + distance * ease(progress));
      
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    }
    
    requestAnimationFrame(animation);
  };

  const scrollToIntro = () => {
    if (introSectionRef.current) {
      const sectionPosition = introSectionRef.current.getBoundingClientRect().top + window.scrollY;

      // Use exact headerHeight without additional offset
      smoothScrollTo(sectionPosition - headerHeight);
    }
  };

  const defaultDescription = "Discover WoodAlps' handcrafted wooden bicycles, made in the French Alps. Experience the perfect blend of traditional craftsmanship and modern technology.";

  // Safely get image URL for hero background
  const getHeroBackgroundImage = () => {
    if (pageData?.heroSection?.backgroundType === 'image' && pageData?.heroSection?.backgroundImage) {
      const imageUrl = urlForImage(pageData.heroSection.backgroundImage);
      return imageUrl || null;
    }
    return null;
  };

  const heroImageUrl = getHeroBackgroundImage();

  return (
    <>
      <SEOHead
        title={pageData?.title || 'WOODALPS - Handcrafted Wooden Bicycles'}
        description={pageData?.description || defaultDescription}
        locale={router.locale}
        path={router.asPath}
        openGraph={{
          image: pageData?.heroSection?.backgroundImage ? urlForImage(pageData.heroSection.backgroundImage) : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://woodalps.com'}/images/logoWA.png`,
          imageAlt: 'WoodAlps handcrafted wooden bicycles'
        }}
      />
      
      <Head>
        <style>{`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-20px);
            }
            60% {
              transform: translateY(-10px);
            }
          }
        `}</style>
      </Head>

      {/* Hero Section with Video Background */}
      <section className="relative w-full overflow-hidden md:h-screen h-auto aspect-video">
        {pageData?.heroSection?.backgroundType === 'image' && heroImageUrl ? (
          <div className="absolute inset-0">
            <Image
              src={heroImageUrl}
              alt={pageData.heroSection.backgroundImage?.alt || 'Hero background'}
              className="md:absolute md:top-0 md:left-0 md:w-full md:h-full md:object-cover w-full h-auto object-contain"
              fill
              loader={sanityLoader}
              sizes="100vw"
              priority
            />
          </div>
        ) : (
          <div className="relative w-full h-full md:absolute md:h-screen">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="md:absolute md:top-0 md:left-0 md:w-full md:h-full md:object-cover w-full h-auto object-contain"
            >
              <source src={pageData?.heroSection?.backgroundVideo || "/videos/hero-background.mp4"} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50"></div>

        {/* Content */}
        <div className="absolute inset-x-0 top-2/3 md:inset-0 flex flex-col items-center justify-center md:justify-center text-center px-4 z-10">
          <h1 className="text-l xs:text-2xl sm:text-3xl md:text-7xl lg:text-8xl font-bold text-white mb-1 md:mb-6">
            {pageData?.heroSection?.title || 'WOODALPS'}
          </h1>
          <p className="text-xs xs:text-sm sm:text-base md:text-2xl lg:text-3xl text-white/90 max-w-2xl">
            {pageData?.heroSection?.subtitle || 'Wooden bikes made in the Alps'}
          </p>
          
          {/* Animated Down Arrow - Hidden on mobile */}
          {pageData?.heroSection?.hasScrollButton !== false && (
            <button 
              onClick={scrollToIntro} 
              className="hidden md:block md:absolute md:bottom-10 md:left-1/2 md:transform md:-translate-x-1/2 text-white cursor-pointer focus:outline-none"
              aria-label="Scroll down"
            >
              {pageData?.heroSection?.scrollButtonText && (
                <p className="text-lg mb-2">{pageData.heroSection.scrollButtonText}</p>
              )}
              <ChevronDown 
                size={32}
                className="animate-bounce" 
                style={{ 
                  animation: 'bounce 1s infinite',
                  opacity: 0.8,
                }} 
              />
            </button>
          )}
        </div>
      </section>

      {/* Rest of the sections */}
      <div ref={introSectionRef}>
        <IntroSection data={pageData?.introSection} />
      </div>
      <ProductLineupSection data={pageData?.productLineupSection} bgColor="bg-white" />
      <ContactSection data={pageData?.contactSection} />
      
      {/* Debug component */}
      <SanityDebug data={pageData} />
    </>
  );
}

// Define a custom getLayout function to apply the layout at the page level
Home.getLayout = function getLayout(page) {
  return <Layout transparentHeader={true} headerShadow={true}>{page}</Layout>;
};

export async function getStaticProps({ preview = false, locale = 'en' }) {
  console.log(`[Homepage] Fetching data for locale: ${locale}`);
  
  const pageData = await getHomePageData(preview, locale);
  
  // Debug log to see what data we're getting
  console.log("Sanity homePageData:", 
    pageData ? 
    `Found with title: ${pageData.title || 'No title'} for locale: ${locale}` : 
    `No data returned for locale: ${locale}`
  );
  
  // Also log if we got any content at all
  if (pageData) {
    console.log("Hero section title:", pageData.heroSection?.title);
    console.log("Hero section subtitle:", pageData.heroSection?.subtitle);
  }
  
  return {
    props: {
      pageData: pageData || null,
      preview,
      locale,
    },
    // Regenerate on every request in preview, otherwise once per day
    revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400,
  };
}